import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import {
  IReviewFormModel,
  reviewFormDefaultValues,
} from "../../models/reviewFormModel";
import { IReviewInviteModel } from "../../models/reviewModels";
import { toReviewPayload } from "../../utils/reviewUtility";

/** The star clicked in the e-mail, when the link carries a valid one. */
const parseRating = (value: string | null): number | null => {
  const rating = Number(value);
  return value && Number.isInteger(rating) && rating >= 1 && rating <= 5
    ? rating
    : null;
};

/**
 * The page the "Как беше?" e-mail opens: /review?token=…&rating=N. No login — the token
 * identifies the client and the studio, and the clicked star is already picked.
 */
export const useReviewInvite = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const linkRating = parseRating(searchParams.get("rating"));
  // What was sent, for the thank-you screen (the stars given and the comment).
  const [submittedReview, setSubmittedReview] = useState<IReviewFormModel | null>(null);

  const { data: invite, isLoading, error } = useApiQuery<IReviewInviteModel>({
    queryKey: [endpointsMetadata.publicReviews.getInvite, token],
    endpoint: `${endpointsMetadata.publicReviews.getInvite}?token=${encodeURIComponent(token)}`,
    enabled: !!token,
    retry: false,
    globalIsLoading: false,
  });

  const { mutate, isLoading: isSaving } = useApiMutation();

  const existingReview = invite?.existingReview ?? null;

  const initialValues = useMemo<IReviewFormModel>(
    () => ({
      rating:
        linkRating ?? existingReview?.rating ?? reviewFormDefaultValues.rating,
      comment: existingReview?.comment ?? reviewFormDefaultValues.comment,
    }),
    [linkRating, existingReview?.rating, existingReview?.comment],
  );

  const submit = useCallback(
    (values: IReviewFormModel) => {
      const payload = toReviewPayload(values);
      mutate(
        {
          endpoint: endpointsMetadata.publicReviews.submitWithInvite,
          method: "post",
          payload: { token, ...payload },
          disableSuccessToast: true,
          // The visitor is usually not logged in; a refusal must not send them to login.
          disable401Redirect: true,
        },
        {
          onSuccess: () =>
            setSubmittedReview({ rating: payload.rating, comment: payload.comment ?? "" }),
        },
      );
    },
    [mutate, token],
  );

  return {
    isLoading: !!token && isLoading,
    isInvalid: !token || (!!error && !invite),
    isSubmitted: !!submittedReview,
    submittedRating: submittedReview?.rating ?? 0,
    submittedComment: submittedReview?.comment ?? "",
    isSaving,
    isEdit: !!existingReview,
    studioId: invite?.studioId,
    studioName: invite?.studioName ?? "",
    googleReviewUrl: invite?.googleReviewUrl ?? null,
    initialValues,
    submit,
  };
};
